import { Modal, Badge } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { KbSolution, VectorizeStatusType } from "@/sharedTypes";

const vectorizeBadgeColor: Record<VectorizeStatusType, string> = {
  not_vectorized: "gray",
  pending: "warning",
  processing: "purple",
  vectorized: "success",
  failed: "failure",
};

interface Props {
  open: boolean;
  onClose: () => void;
  solution: KbSolution | null;
}

export default function KbSolutionDetailModal({ open, onClose, solution }: Props) {
  const { t } = useTranslation(["knowledgeBase"]);

  const linkedIssues = solution?.issues || [];

  return (
    <Modal show={open} onClose={onClose} size="xl">
      <Modal.Header>{t("knowledgeBase:solution")}</Modal.Header>
      <Modal.Body>
        {!solution ? (
          <p className="text-center text-gray-500 py-8">Loading...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("knowledgeBase:solutionText")}
              </p>
              <p className="mt-1 text-gray-900 dark:text-white whitespace-pre-wrap">
                {solution.kb_solution_text}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t("knowledgeBase:aiApproved")}
                </p>
                <Badge color={solution.is_ai_approved ? "success" : "gray"} size="sm" className="mt-1 w-fit">
                  {solution.is_ai_approved ? t("knowledgeBase:yes") : t("knowledgeBase:no")}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t("knowledgeBase:vectorized")}
                </p>
                <Badge
                  color={vectorizeBadgeColor[solution.vectorize_status || "not_vectorized"]}
                  size="sm"
                  className="mt-1 w-fit"
                >
                  {t(`knowledgeBase:vectorizeStatus.${solution.vectorize_status || "not_vectorized"}`)}
                </Badge>
              </div>
            </div>

            <hr className="dark:border-gray-700" />

            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                {t("knowledgeBase:linkedIssues")} ({linkedIssues.length})
              </p>
              {linkedIssues.length > 0 ? (
                <div className="space-y-2">
                  {linkedIssues.map((issue) => (
                    <div
                      key={issue.id}
                      className="rounded-lg border border-gray-200 p-3 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {issue.kb_issue_text}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">{t("knowledgeBase:noIssues")}</p>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
